import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  Grid,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Alert,
  CircularProgress,
  InputAdornment,
} from '@mui/material';
import {
  Search as SearchIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { usersApi } from '../services/api';
import { UserFilters } from '../types/api';
import UsersTable from '../components/UsersTable';
import { useDebounce } from '../hooks/useDebounce';

export default function Users() {
  const [filters, setFilters] = useState<UserFilters>({
    page: 1,
    size: 20,
    search: '',
    subscription_filter: '',
    language: '',
    blocked: undefined,
    sort_field: 'created_at',
    sort_direction: 'desc',
  });

  // Дебаунс для поиска
  const debouncedSearch = useDebounce(filters.search || '', 500);

  // Применяем дебаунс к фильтрам
  const debouncedFilters = {
    ...filters,
    search: debouncedSearch,
  };

  // Загрузка опций для фильтров
  const { data: filterOptions } = useQuery({
    queryKey: ['filterOptions'],
    queryFn: usersApi.getFilterOptions,
    staleTime: 5 * 60 * 1000, // Кэшируем на 5 минут
  });

  const { data, isLoading, error } = useQuery({
    queryKey: ['users', debouncedFilters],
    queryFn: () => usersApi.getUsers(debouncedFilters),
    keepPreviousData: true,
  });

  const { data: userStats } = useQuery({
    queryKey: ['userStats'],
    queryFn: usersApi.getUserStats,
    refetchInterval: 30000,
  });

  const handleFilterChange = (newFilters: Partial<UserFilters>) => {
    setFilters(prev => ({
      ...prev,
      ...newFilters,
      page: 1, // Сброс на первую страницу при изменении фильтров
    }));
  };

  const clearFilters = () => {
    setFilters({
      page: 1,
      size: 20,
      search: '',
      subscription_filter: '',
      language: '',
      blocked: undefined,
      sort_field: 'created_at',
      sort_direction: 'desc',
    });
  };

  const activeFiltersCount = [
    filters.search,
    filters.subscription_filter,
    filters.language,
    filters.blocked !== undefined,
  ].filter(Boolean).length;

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        Ошибка загрузки пользователей: {(error as Error).message}
      </Alert>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 600 }}>
          Управление пользователями
        </Typography>

        {userStats && (
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Chip label={`Всего: ${userStats.total_users}`} variant="outlined" />
            <Chip label={`Активные: ${userStats.active_subscriptions}`} color="success" variant="outlined" />
            <Chip label={`Заблокированы: ${userStats.blocked_users}`} color="error" variant="outlined" />
          </Box>
        )}
      </Box>

      {/* Фильтры */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">
              Поиск и фильтры
              {activeFiltersCount > 0 && (
                <Chip
                  size="small"
                  label={`${activeFiltersCount} активных`}
                  color="primary"
                  sx={{ ml: 2 }}
                />
              )}
            </Typography>
            {activeFiltersCount > 0 && (
              <Chip
                label="Очистить фильтры"
                variant="outlined"
                size="small"
                onDelete={clearFilters}
                deleteIcon={<ClearIcon />}
                onClick={clearFilters}
              />
            )}
          </Box>

          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Поиск по имени, username или Telegram ID"
                value={filters.search || ''}
                onChange={(e) => handleFilterChange({ search: e.target.value })}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
                placeholder="Введите имя, @username или ID"
              />
            </Grid>

            <Grid item xs={12} md={2}>
              <FormControl fullWidth>
                <InputLabel>Подписка</InputLabel>
                <Select
                  value={filters.subscription_filter || ''}
                  label="Подписка"
                  onChange={(e) => handleFilterChange({ subscription_filter: e.target.value || '' })}
                >
                  {filterOptions?.subscription_filters.map((filter) => (
                    <MenuItem key={filter} value={filter === 'Все' ? '' : filter}>
                      {filter}
                    </MenuItem>
                  )) || [
                    <MenuItem key="all" value="">Все</MenuItem>,
                    <MenuItem key="with" value="С подпиской">С подпиской</MenuItem>,
                    <MenuItem key="without" value="Без подписки">Без подписки</MenuItem>,
                    <MenuItem key="weekly" value="Недельная">Недельная</MenuItem>,
                    <MenuItem key="monthly" value="Месячная">Месячная</MenuItem>,
                    <MenuItem key="manual" value="Ручная">Ручная</MenuItem>,
                  ]}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={2}>
              <FormControl fullWidth>
                <InputLabel>Язык</InputLabel>
                <Select
                  value={filters.language || ''}
                  label="Язык"
                  onChange={(e) => handleFilterChange({ language: e.target.value || '' })}
                >
                  <MenuItem value="">Все</MenuItem>
                  {filterOptions?.languages.map((lang) => (
                    <MenuItem key={lang} value={lang}>
                      {lang === 'ru' ? 'Русский' : lang === 'en' ? 'English' : lang}
                    </MenuItem>
                  )) || [
                    <MenuItem key="ru" value="ru">Русский</MenuItem>,
                    <MenuItem key="en" value="en">English</MenuItem>,
                  ]}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={2}>
              <FormControl fullWidth>
                <InputLabel>Статус</InputLabel>
                <Select
                  value={filters.blocked !== undefined ? String(filters.blocked) : ''}
                  label="Статус"
                  onChange={(e) => handleFilterChange({
                    blocked: e.target.value === '' ? undefined : e.target.value === 'true'
                  })}
                >
                  <MenuItem value="">Все</MenuItem>
                  <MenuItem value="false">Активные</MenuItem>
                  <MenuItem value="true">Заблокированные</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Таблица */}
      <Card>
        <CardContent sx={{ p: 0 }}>
          {isLoading && !data ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <UsersTable
              users={data?.items || []}
              total={data?.total || 0}
              loading={isLoading}
              filters={filters}
              onFiltersChange={setFilters}
            />
          )}
        </CardContent>
      </Card>
    </Box>
  );
}
